import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

type Media = {
    media?: {
        file?: string | { url?: string };
    };
};

type Props = {
    data: {
        ["Page Heading"]?: string;

        title1?: string;
        description1?: string;
        image1?: Media;

        title2?: string;
        description2?: string;
        image2?: Media;

        title3?: string;
        description3?: string;
        image3?: Media;
    };
};

const getImageUrl = (image?: Media): string | undefined => {
    if (!image) return undefined;

    if (typeof image.media?.file === "string") {
        return image.media.file;
    }

    if (image.media?.file && typeof image.media.file === "object") {
        return image.media.file.url;
    }

    return undefined;
};

const ProwhipCustomerService: React.FC<Props> = ({ data }) => {
    const cards = [
        {
            title: data.title1,
            description: data.description1,
            imageUrl: getImageUrl(data.image1),
        },
        {
            title: data.title2,
            description: data.description2,
            imageUrl: getImageUrl(data.image2),
        },
        {
            title: data.title3,
            description: data.description3,
            imageUrl: getImageUrl(data.image3),
        },
    ].filter((card) => card.title || card.description);

    return (
        <section className="py-24 px-6 bg-[#f3f3f3]">
            {/* Page Heading */}

            {data["Page Heading"] && (
                <h2 className="text-3xl md:text-[40px] font-medium text-center text-gray-800 mb-20">
                    {data["Page Heading"]}
                </h2>
            )}

            {/* Cards */}

            <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
                {cards.map((card, index) => (
                    <div
                        key={index}
                        className="relative rounded-xl overflow-hidden shadow-md min-h-[360px]"
                    >
                        {/* Background Image */}

                        {card.imageUrl && (
                            <div
                                className="absolute inset-0 bg-cover bg-center opacity-40"
                                style={{
                                    backgroundImage: `url(${card.imageUrl})`,
                                }}
                            />
                        )}

                        {/* Overlay */}

                        <div className="absolute inset-0 bg-white/90" />

                        {/* Content */}

                        <div className="relative p-8 flex flex-col h-full">
                            {card.title && (
                                <h3 className="text-lg font-semibold mb-6 underline decoration-yellow-400 decoration-2 underline-offset-4">
                                    {card.title}
                                </h3>
                            )}

                            {card.description && (
                                <ReactMarkdown
                                    rehypePlugins={[rehypeRaw]}
                                    className="text-[15px] text-gray-700 leading-relaxed"
                                >
                                    {card.description}
                                </ReactMarkdown>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default ProwhipCustomerService;
